if (!customElements.get('hero-slideshow')) {
  customElements.define(
    'hero-slideshow',
    class HeroSlideshow extends HTMLElement {
      constructor() {
        super();
        this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
        this.bars = Array.from(this.querySelectorAll('.hero-slideshow__bar'));
        this.track = this.querySelector('.hero-slideshow__slides');
        this.autoplayButton = this.querySelector('.hero-slideshow__autoplay');

        this.current = 0;
        this.count = this.slides.length;
        this.transition = this.dataset.transition || 'fade';
        this.speed = parseInt(this.dataset.speed, 10) || 5000;
        this.clickable = this.dataset.clickable === 'true';

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayEnabled = this.dataset.autoplay === 'true' && this.count > 1 && !this.reducedMotion;

        this.timer = null;
        this.timerStart = 0;
        this.remaining = this.speed;
        this.userPaused = false;
      }

      connectedCallback() {
        if (this.count <= 1) return;

        this.bindEvents();
        if (this.transition === 'slide') this.updateTransforms();

        if (this.autoplayEnabled) {
          this.play();
        }
      }

      disconnectedCallback() {
        this.stopTimer();
      }

      bindEvents() {
        this.querySelectorAll('.hero-slideshow__arrow--prev').forEach((el) =>
          el.addEventListener('click', () => this.step(-1))
        );
        this.querySelectorAll('.hero-slideshow__arrow--next').forEach((el) =>
          el.addEventListener('click', () => this.step(1))
        );

        if (this.clickable) {
          this.bars.forEach((bar) => {
            const index = parseInt(bar.dataset.index, 10);
            bar.addEventListener('click', () => this.goTo(index));
          });
        }

        if (this.autoplayButton) {
          this.autoplayButton.addEventListener('click', () => this.toggleAutoplay());
        }

        /* Pause while the pointer rests on the slideshow */
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());

        /* Pause when the tab is hidden to keep the progress bar in sync */
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.pause();
          } else {
            this.resume();
          }
        });

        this.bindDrag();
        this.bindThemeEditor();
      }

      /* Pointer-based dragging: works with mouse, touch and pen.
         touch-action: pan-y (set in CSS) keeps vertical page scrolling working. */
      bindDrag() {
        const el = this.track;
        const threshold = 50;
        let startX = 0;
        let startY = 0;
        let pointerId = null;
        let moved = false;

        el.addEventListener('pointerdown', (event) => {
          if (event.button && event.button !== 0) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          moved = false;
          this.classList.add('is-dragging');
          this.pause();
        });

        el.addEventListener('pointermove', (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) moved = true;
        });

        const end = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          pointerId = null;
          this.classList.remove('is-dragging');
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;

          if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
            this.step(dx < 0 ? 1 : -1);
            /* Swallow the click that follows a drag so it doesn't trigger a slide link */
            el.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, {
              capture: true,
              once: true,
            });
          } else {
            this.resume();
          }
        };

        el.addEventListener('pointerup', end);
        el.addEventListener('pointercancel', end);
        el.addEventListener('pointerleave', end);
      }

      bindThemeEditor() {
        if (!window.Shopify || !window.Shopify.designMode) return;

        this.addEventListener('shopify:block:select', (event) => {
          const slide = this.slides.find((s) => s.dataset.blockId === event.detail.blockId);
          if (slide) this.goTo(parseInt(slide.dataset.index, 10));
          this.pause();
        });
        this.addEventListener('shopify:block:deselect', () => this.resume());
      }

      step(direction) {
        let next = this.current + direction;
        if (next < 0) next = this.count - 1;
        if (next > this.count - 1) next = 0;
        this.goTo(next);
      }

      goTo(index) {
        if (index === this.current) return;
        this.current = index;
        this.render();
        if (this.autoplayEnabled && !this.userPaused) this.startTimer(this.speed);
      }

      render() {
        this.slides.forEach((slide, i) => {
          const active = i === this.current;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.bars.forEach((bar, i) => {
          bar.classList.remove('is-active', 'is-past');
          if (i < this.current) bar.classList.add('is-past');
        });

        if (this.transition === 'slide') this.updateTransforms();

        /* Force the active bar's fill animation to restart from zero */
        const activeBar = this.bars[this.current];
        if (activeBar) {
          void activeBar.offsetWidth;
          activeBar.classList.add('is-active');
        }
      }

      updateTransforms() {
        this.slides.forEach((slide, i) => {
          slide.style.transform = `translateX(${(i - this.current) * 100}%)`;
        });
      }

      /* ---------- Autoplay timing ---------- */
      play() {
        this.classList.add('is-playing');
        this.classList.remove('is-paused');
        this.startTimer(this.speed);
      }

      startTimer(duration) {
        this.stopTimer();
        this.timerStart = Date.now();
        this.remaining = duration;
        this.timer = setTimeout(() => this.step(1), duration);
      }

      stopTimer() {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
      }

      pause() {
        if (!this.autoplayEnabled || this.userPaused || !this.timer) return;
        this.remaining -= Date.now() - this.timerStart;
        this.stopTimer();
        this.classList.add('is-paused');
      }

      resume() {
        if (!this.autoplayEnabled || this.userPaused) return;
        this.classList.remove('is-paused');
        this.startTimer(this.remaining > 0 ? this.remaining : this.speed);
      }

      toggleAutoplay() {
        if (!this.autoplayButton) return;
        if (this.userPaused) {
          this.userPaused = false;
          this.autoplayButton.dataset.state = 'playing';
          this.autoplayButton.setAttribute(
            'aria-label',
            this.autoplayButton.getAttribute('data-label-pause') || 'Pause slideshow'
          );
          this.play();
        } else {
          this.userPaused = true;
          this.autoplayButton.dataset.state = 'paused';
          this.stopTimer();
          this.classList.remove('is-playing', 'is-paused');
        }
      }
    }
  );
}
